import { useCallback, useEffect, useRef, useState } from "react";
import ExcelJS from "exceljs";
import { Download, Upload, FileText, AlertCircle, CheckCircle, ChevronLeft, ChevronRight, RotateCcw, Loader2, X, Printer } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { useUrlSearchParam } from "../hooks/useUrlSearchParam";
import FilterPills from "./admin/FilterPills";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";
import { useWarehouseScope } from "../access/useWarehouseScope";
import SortingLabelPrintModal, { type BayLabel } from "./SortingLabelPrintModal";
import { TOTE_LABEL_BASE, readToteLabelSize, saveToteLabelSize, toteLabel } from "./helpers/toteLabel";

const PAGE_SIZE = 25;

interface ToteRow {
    tote_id: string;
    status: string;
    type: string;
    created_at?: string;
    // Derived server-side from the latest book_ins / put_aways row for
    // this tote — null until the tote has been used at least once.
    warehouse?: string | null;
    last_user?: string | null;
    updated_at?: string | null;
}

// Pseudo-warehouse pills that expand into a list of real warehouses
// when the BE call goes out. Mirrors the InventoryAdmin convention so
// the same group expansion applies to both pages.
const WAREHOUSE_GROUPS: Record<string, string[]> = {
    "group:UK": ["Newport", "Pembroke"],
};
const expandWarehouseFilter = (raw: string): string[] => {
    if (!raw) return [];
    return WAREHOUSE_GROUPS[raw] ?? [raw];
};

interface BulkResult {
    added: number;
    updated: number;
    removed: number;
    errors: { row: number; reason: string }[];
}

// Identifier columns that round-trip as Text — tote_id can be a numeric
// string with leading zeros (e.g. "001"), which Excel would otherwise
// auto-coerce. type is enum-string so safe either way; included so the
// column header doesn't pick up a stale general format on hand-built
// uploads.
const TEXT_COLUMNS = new Set<string>(["tote_id", "type"]);

const cellToString = (cell: ExcelJS.Cell | undefined): string => {
    if (!cell) return "";
    const v = cell.value;
    if (v === null || v === undefined) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === "object") {
        if ("result" in v && (v as { result?: unknown }).result != null) {
            return String((v as { result: unknown }).result);
        }
        if ("text" in v && (v as { text?: unknown }).text != null) {
            return String((v as { text: unknown }).text);
        }
        if ("richText" in v) {
            const rt = (v as { richText?: { text: string }[] }).richText;
            return Array.isArray(rt) ? rt.map(r => r.text).join("") : "";
        }
    }
    return String(v);
};

const downloadXlsx = async (filename: string, header: readonly string[], rows: string[][]) => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Totes");
    ws.addRow(header as string[]);
    for (const r of rows) ws.addRow(r);
    header.forEach((name, i) => {
        if (TEXT_COLUMNS.has(name)) ws.getColumn(i + 1).numFmt = "@";
    });
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const readXlsxRows = async (file: File): Promise<{ header: string[]; rows: string[][] }> => {
    const buffer = await file.arrayBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buffer);
    const ws = wb.worksheets[0];
    if (!ws || ws.rowCount < 1) return { header: [], rows: [] };
    const headerRow = ws.getRow(1);
    const colCount = headerRow.cellCount;
    const header: string[] = [];
    for (let c = 1; c <= colCount; c++) {
        header[c - 1] = cellToString(headerRow.getCell(c)).trim().toLowerCase();
    }
    const rows: string[][] = [];
    for (let r = 2; r <= ws.rowCount; r++) {
        const xrow = ws.getRow(r);
        const cells: string[] = [];
        for (let c = 1; c <= colCount; c++) cells[c - 1] = cellToString(xrow.getCell(c));
        if (cells.some(s => s.trim() !== "")) rows.push(cells);
    }
    return { header, rows };
};

const CHUNK_SIZE = 500;
const STATUS_KEYS = ["available", "open", "closed", "stored"] as const;
const TYPE_KEYS = ["box", "pallet", "york"] as const;

// Date helper for the table column — ISO comes off the server, render
// in the user's locale at day-precision (created_at to-the-second isn't
// useful for an admin scanning the list).
const formatDate = (iso?: string) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(); }
    catch { return iso; }
};

// dd/mm/yyyy hh:mm:ss in local time — matches the inventory pages so
// admins moving between Totes / Inventory see one date format.
const formatDateTime = (iso?: string | null) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} `
        + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const TotesAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [totes, setTotes] = useState<ToteRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
    const [lastResult, setLastResult] = useState<BulkResult | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Filters + pagination — same UX shape as LocationsAdmin so admins
    // moving between pages see one consistent list interface. Search
    // requires Enter (or blur with a change) so the BE isn't hit per
    // keystroke. `?search=` URL param lets AdminSearch deep-link in
    // pre-filtered. searchInput tracks keystrokes locally; search is
    // the committed value used for the BE call.
    const [search, setSearch] = useUrlSearchParam("search");
    const [searchInput, setSearchInput] = useState(search);
    useEffect(() => { setSearchInput(search); }, [search]);
    const [statusFilter, setStatusFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    // Warehouse filter — fed by /settings on mount; values are real
    // warehouse names plus optional "group:UK" entries that expand to a
    // list. Latest-activity-warehouse on the BE side, so totes that
    // aren't currently 'stored' still surface under the warehouse where
    // they were last booked-in.
    const warehouseScope = useWarehouseScope();
    const [pickedWarehouse, setWarehouseFilter] = useState("");
    // Without cross_warehouse the list is pinned to the person's own warehouse (no All, no groups).
    const warehouseFilter = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pickedWarehouse;
    const [allWarehouses, setWarehouses] = useState<string[]>([]);
    const warehouses = warehouseScope.limit(allWarehouses);
    // User filter — Enter-to-submit free-text substring against the
    // tote's last interaction (started_by / completed_by, whichever is
    // later).
    const [userFilter, setUserFilter] = useState("");
    const [userInput, setUserInput] = useState("");
    const [page, setPage] = useState(0);

    // Reset-to-available confirmation state. pendingReset pins the row
    // so the modal has full context; resettingToteId guards the button
    // while the request is in flight.
    const [pendingReset, setPendingReset] = useState<ToteRow | null>(null);
    const [resettingToteId, setResettingToteId] = useState<string | null>(null);

    useEffect(() => { setPage(0); }, [search, statusFilter, typeFilter, warehouseFilter, userFilter]);

    // Warehouses for the pill row — loaded once on mount.
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) return;
                const data: { warehouse: string }[] = await res.json();
                setWarehouses(data.map(r => r.warehouse).sort());
            } catch { /* leave empty */ }
        })();
    }, []);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search.trim()) params.set("q", search.trim());
            if (statusFilter) params.set("status", statusFilter);
            if (typeFilter) params.set("type", typeFilter);
            const expandedWh = expandWarehouseFilter(warehouseFilter);
            if (expandedWh.length > 0) params.set("warehouse", expandedWh.join(","));
            if (userFilter.trim()) params.set("user", userFilter.trim());
            params.set("page", String(page));
            params.set("page_size", String(PAGE_SIZE));
            const res = await apiFetch(`/book-in/totes?${params}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            // BE may return {totes,total} (new) or a bare array (legacy).
            // Tolerate both so we can roll out without an atomic FE/BE swap.
            const list: ToteRow[] = Array.isArray(data) ? data : (data?.totes ?? []);
            setTotes(list);
            setTotal(typeof data?.total === "number" ? data.total : list.length);
        } catch {
            toast.error(t("server_error"), { toastId: "totes-load-err" });
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter, typeFilter, warehouseFilter, userFilter, page, t]);

    useEffect(() => { refresh(); }, [refresh]);

    // Reset a single tote back to 'available' — flips totes.status and
    // cancels any in_progress book_ins, mirroring the per-warehouse
    // maintenance script but scoped to one tote. Confirmation lives in
    // pendingReset state so the modal has full row context.
    const confirmReset = async () => {
        if (!pendingReset) return;
        const toteId = pendingReset.tote_id;
        setResettingToteId(toteId);
        try {
            const res = await apiFetch(
                `/book-in/totes/${encodeURIComponent(toteId)}/reset`,
                { method: "POST" },
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            toast.success(t("totes_reset_done", { toteId }), { toastId: `tote-reset-${toteId}` });
            setPendingReset(null);
            await refresh();
        } catch {
            toast.error(t("totes_reset_failed"), { toastId: `tote-reset-${toteId}` });
        } finally {
            setResettingToteId(null);
        }
    };

    const downloadTemplate = async () => {
        const rows: string[][] = [
            ["add", "TOTE100", "box"],
            ["add", "PAL100", "pallet"],
            ["add", "YRK100", "york"],
            ["update", "TOTE001", "pallet"],
            ["remove", "TOTE999", ""],
        ];
        await downloadXlsx("totes-template.xlsx", ["action", "tote_id", "type"], rows);
    };

    // Every tote matching the current filters, not just the visible 25-row
    // page slice — paged through the BE, which caps page_size at 200 (book-in
    // controller listTotes). Shared by Download Current and Print labels.
    const fetchAllFiltered = async (): Promise<ToteRow[]> => {
        const DOWNLOAD_PAGE_SIZE = 200;
        const baseParams = new URLSearchParams();
        if (search.trim()) baseParams.set("q", search.trim());
        if (statusFilter) baseParams.set("status", statusFilter);
        if (typeFilter) baseParams.set("type", typeFilter);
        const expandedWh = expandWarehouseFilter(warehouseFilter);
        if (expandedWh.length > 0) baseParams.set("warehouse", expandedWh.join(","));
        if (userFilter.trim()) baseParams.set("user", userFilter.trim());
        baseParams.set("page_size", String(DOWNLOAD_PAGE_SIZE));

        const all: ToteRow[] = [];
        let pageNum = 0;
        while (true) {
            const params = new URLSearchParams(baseParams);
            params.set("page", String(pageNum));
            const res = await apiFetch(`/book-in/totes?${params}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const list: ToteRow[] = Array.isArray(data) ? data : (data?.totes ?? []);
            all.push(...list);
            const total = typeof data?.total === "number" ? data.total : all.length;
            if (list.length === 0 || all.length >= total) break;
            pageNum += 1;
        }
        return all;
    };

    const downloadCurrent = async () => {
        try {
            const all = await fetchAllFiltered();
            const rows: string[][] = all.map(r => ["", r.tote_id, r.type, r.status]);
            await downloadXlsx("totes-current.xlsx", ["action", "tote_id", "type", "status"], rows);
        } catch {
            toast.error(t("server_error"), { toastId: "totes-download-err" });
        }
    };

    const onFileChosen = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const file = ev.target.files?.[0];
        if (!file) return;
        ev.target.value = "";

        let parsed: { header: string[]; rows: string[][] };
        try {
            parsed = await readXlsxRows(file);
        } catch {
            toast.error(t("totes_upload_parse_error"), { toastId: "totes-parse" });
            return;
        }
        const { header, rows: dataRows } = parsed;
        if (dataRows.length === 0) {
            toast.error(t("totes_upload_empty"), { toastId: "totes-empty" });
            return;
        }
        const actionIdx = header.indexOf("action");
        const idIdx = header.indexOf("tote_id");
        const typeIdx = header.indexOf("type");
        if (actionIdx === -1 || idIdx === -1) {
            toast.error(t("totes_upload_bad_header"), { toastId: "totes-hdr" });
            return;
        }

        const rows = dataRows.map(r => ({
            action: r[actionIdx] ?? "",
            tote_id: r[idIdx] ?? "",
            type: typeIdx >= 0 ? (r[typeIdx] ?? "") : "",
        }));

        setUploading(true);
        setProgress({ done: 0, total: rows.length });
        setLastResult(null);

        const combined: BulkResult = { added: 0, updated: 0, removed: 0, errors: [] };
        try {
            for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
                const chunk = rows.slice(i, i + CHUNK_SIZE);
                const res = await apiFetch(`/book-in/totes/bulk`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ rows: chunk }),
                });
                // 413 = chunk body exceeds the BE's JSON limit. Stop the
                // upload (subsequent chunks will fail the same way) and
                // surface a specific message so the operator knows it's
                // a size issue.
                if (res.status === 413) {
                    toast.error(t("bulk_upload_too_large"), { toastId: "totes-upload-413" });
                    break;
                }
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const chunkResult: BulkResult = await res.json();

                combined.added += chunkResult.added;
                combined.updated += chunkResult.updated;
                combined.removed += chunkResult.removed;
                for (const e of chunkResult.errors) {
                    combined.errors.push({ row: e.row + i, reason: e.reason });
                }

                setProgress({ done: Math.min(i + chunk.length, rows.length), total: rows.length });
            }
            setLastResult(combined);
            await refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "totes-upload-err" });
        } finally {
            setUploading(false);
            setProgress(null);
        }
    };

    // BE returns the page slice already filtered + searched, so the
    // visible rows are just the response. No client-side useMemo or
    // slicing needed — pagination math reads the BE total instead.
    // Tote label print. null = closed; otherwise the labels the modal lists
    // (all ticked) — the whole filtered list from the toolbar, or one tote
    // from its row button.
    const [printLabels, setPrintLabels] = useState<BayLabel[] | null>(null);
    const [loadingPrint, setLoadingPrint] = useState(false);
    const [toteLabelSize, setToteLabelSize] = useState(readToteLabelSize);
    const openPrintAll = async () => {
        setLoadingPrint(true);
        try {
            setPrintLabels((await fetchAllFiltered()).map(toteLabel));
        } catch {
            toast.error(t("server_error"), { toastId: "totes-print-load-err" });
        } finally {
            setLoadingPrint(false);
        }
    };
    const saveSize = async (w: number | null, h: number | null) => {
        if (!saveToteLabelSize(w, h)) return false;
        setToteLabelSize({ widthMm: w, heightMm: h });
        return true;
    };

    const visible = totes;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const safePage = Math.min(page, Math.max(totalPages - 1, 0));
    const rangeFrom = total === 0 ? 0 : safePage * PAGE_SIZE + 1;
    const rangeTo = Math.min((safePage + 1) * PAGE_SIZE, total);

    return (
        <div className="totes-admin">
            <div className="totes-admin-actions">
                <button type="button" className="btn btn-ghost" onClick={downloadTemplate}>
                    <FileText size={16} /> {t("totes_download_template")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={downloadCurrent}
                    disabled={loading || totes.length === 0}
                >
                    <Download size={16} /> {t("totes_download_current")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={openPrintAll}
                    disabled={loading || loadingPrint || total === 0}
                >
                    {loadingPrint ? <Loader2 size={16} className="dispatch-admin-spin" /> : <Printer size={16} />}
                    {" "}{t("totes_print_labels_n", { count: String(total) })}
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : undefined}
                >
                    <Upload size={16} /> {uploading ? t("submitting") : t("totes_upload_xlsx")}
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{ display: "none" }}
                    onChange={onFileChosen}
                />
            </div>

            {progress && (
                <div className="totes-admin-progress">
                    <div className="totes-admin-progress-label">
                        {t("totes_upload_progress", {
                            done: String(progress.done),
                            total: String(progress.total),
                        })}
                        <span className="totes-admin-progress-pct">
                            {Math.round((progress.done / Math.max(progress.total, 1)) * 100)}%
                        </span>
                    </div>
                    <div className="totes-admin-progress-track">
                        <div
                            className="totes-admin-progress-fill"
                            style={{ width: `${(progress.done / Math.max(progress.total, 1)) * 100}%` }}
                        />
                    </div>
                </div>
            )}

            {lastResult && (
                <div className="totes-admin-result">
                    <div className="totes-admin-result-line">
                        <CheckCircle strokeWidth={3} size={16} className="totes-admin-result-ok" />
                        <span>
                            {t("totes_bulk_summary", {
                                added: String(lastResult.added),
                                updated: String(lastResult.updated),
                                removed: String(lastResult.removed),
                            })}
                        </span>
                    </div>
                    {lastResult.errors.length > 0 && (
                        <ul className="totes-admin-errors">
                            {lastResult.errors.map((e, i) => (
                                <li key={i}>
                                    <AlertCircle size={14} />
                                    <span>{t("totes_row_label", { row: String(e.row) })}: {e.reason}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {!loading && (
                <>
                    {/* Compact one-row summary strip — same shape as
                        .locations-summary so the admin pages feel
                        unified. Total reflects the current filter (BE
                        side); per-status / per-type counts dropped
                        because the BE only returns the filtered page,
                        not whole-dataset breakdowns. */}
                    <div className="locations-summary">
                        <div className="locations-summary-total">
                            <span className="locations-summary-total-label">{t("totes_total_label")}</span>
                            <strong>{total}</strong>
                        </div>
                    </div>

                    <FilterPills
                        className="warehouse-admin-pills"
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={STATUS_KEYS.map(s => ({
                            value: s,
                            label: t(`tote_status_${s}_filter`),
                        }))}
                    />

                    {/* Warehouse pills — group rows ("UK") sit at the
                        front, individual warehouses follow. Reuses the
                        same FilterPills control as status above. */}
                    <FilterPills
                        className="warehouse-admin-pills"
                        value={warehouseFilter}
                        onChange={setWarehouseFilter}
                        hideAll={!warehouseScope.canCross}
                        options={[
                            ...(!warehouseScope.canCross ? [] : Object.entries(WAREHOUSE_GROUPS))
                                .filter(([, members]) => members.some(m => warehouses.includes(m)))
                                .map(([groupKey]) => ({
                                    value: groupKey,
                                    label: groupKey.replace(/^group:/, ""),
                                })),
                            ...warehouses.map(w => ({ value: w, label: w })),
                        ]}
                    />

                    <div className="warehouse-admin-filters users-admin-filters">
                        <input
                            className="locations-table-search wh-admin-filter-search"
                            type="search"
                            placeholder={t("totes_search_hint")}
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    setSearch(searchInput.trim());
                                }
                            }}
                            onBlur={() => {
                                if (searchInput.trim() !== search.trim()) {
                                    setSearch(searchInput.trim());
                                }
                            }}
                        />
                        <input
                            className="locations-table-search wh-admin-filter-user"
                            type="search"
                            placeholder={t("totes_user_search_hint")}
                            value={userInput}
                            onChange={e => setUserInput(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    setUserFilter(userInput.trim());
                                }
                            }}
                            onBlur={() => {
                                if (userInput.trim() !== userFilter.trim()) {
                                    setUserFilter(userInput.trim());
                                }
                            }}
                        />
                        <select
                            className="locations-table-search wh-admin-filter-select"
                            value={typeFilter}
                            onChange={e => setTypeFilter(e.target.value)}
                        >
                            <option value="">{t("totes_filter_all_types")}</option>
                            {TYPE_KEYS.map(ty => <option key={ty} value={ty}>{ty}</option>)}
                        </select>
                    </div>

                    {total === 0 ? (
                        <div className="locations-table-empty">{t("totes_no_matches")}</div>
                    ) : (
                        <>
                            <div className="locations-table-wrap">
                                <table className="locations-table totes-admin-table">
                                    <thead>
                                        <tr>
                                            <th>{t("totes_col_id")}</th>
                                            <th>{t("totes_col_type")}</th>
                                            <th>{t("totes_col_status")}</th>
                                            <th>{t("totes_col_warehouse")}</th>
                                            <th>{t("totes_col_last_user")}</th>
                                            <th>{t("totes_col_updated_at")}</th>
                                            <th>{t("totes_col_created")}</th>
                                            <th aria-hidden="true" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visible.map(r => (
                                            <tr key={r.tote_id}>
                                                <td className="locations-table-mono">{r.tote_id}</td>
                                                <td>{r.type}</td>
                                                <td>{r.status}</td>
                                                <td>{r.warehouse || "—"}</td>
                                                <td>{r.last_user || "—"}</td>
                                                <td>{formatDateTime(r.updated_at)}</td>
                                                <td>{formatDate(r.created_at)}</td>
                                                <td className="locations-table-actions">
                                                    <button
                                                        type="button"
                                                        className="locations-table-btn"
                                                        onClick={() => setPrintLabels([toteLabel(r)])}
                                                        aria-label={t("totes_print_label")}
                                                        title={t("totes_print_label")}
                                                    >
                                                        <Printer size={14} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="locations-table-btn"
                                                        onClick={() => setPendingReset(r)}
                                                        disabled={r.status === "available" || resettingToteId === r.tote_id || readOnly}
                                                        aria-label={t("totes_reset_action")}
                                                        title={readOnly ? t("read_only_mode") : t("totes_reset_action")}
                                                    >
                                                        {resettingToteId === r.tote_id
                                                            ? <Loader2 size={14} className="dispatch-admin-spin" />
                                                            : <RotateCcw size={14} />}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="locations-table-footer">
                                <span>{t("locations_showing_range", {
                                    from: String(rangeFrom),
                                    to: String(rangeTo),
                                    total: String(total),
                                })}</span>
                                <div className="locations-table-pagination">
                                    <button
                                        type="button"
                                        className="locations-table-page-btn"
                                        onClick={() => setPage(p => Math.max(0, p - 1))}
                                        disabled={safePage === 0}
                                        aria-label={t("pagination_previous")}
                                    >
                                        <ChevronLeft size={16} />
                                        <span>{t("pagination_previous")}</span>
                                    </button>
                                    <span className="locations-table-page-indicator">
                                        {t("pagination_page_x_of_y", {
                                            page: String(safePage + 1),
                                            total: String(totalPages),
                                        })}
                                    </span>
                                    <button
                                        type="button"
                                        className="locations-table-page-btn"
                                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                        disabled={safePage >= totalPages - 1}
                                        aria-label={t("pagination_next")}
                                    >
                                        <span>{t("pagination_next")}</span>
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}

            {pendingReset && (
                <div
                    className="modal-overlay"
                    onClick={() => resettingToteId == null && setPendingReset(null)}
                >
                    <div
                        className="modal-card locations-delete-modal"
                        onClick={e => e.stopPropagation()}
                        role="alertdialog"
                        aria-labelledby="totes-reset-title"
                    >
                        <div className="modal-card-header">
                            <h2 id="totes-reset-title">{t("totes_reset_title")}</h2>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={() => setPendingReset(null)}
                                disabled={resettingToteId != null}
                                aria-label={t("close")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p className="locations-delete-body">
                                {t("totes_reset_body", { toteId: pendingReset.tote_id })}
                            </p>
                            <p className="locations-delete-hint">{t("totes_reset_hint")}</p>
                            <div className="modal-confirm-buttons">
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => setPendingReset(null)}
                                    disabled={resettingToteId != null}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={confirmReset}
                                    disabled={resettingToteId != null || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    <RotateCcw size={14} />
                                    {resettingToteId != null ? t("submitting") : t("totes_reset_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {printLabels && (
                <SortingLabelPrintModal
                    warehouse={warehouseFilter}
                    title={t("totes_print_labels_title")}
                    hint={printLabels.length === 1
                        ? t("totes_print_label_hint", { tote: printLabels[0].label })
                        : t("totes_print_labels_hint", { count: String(printLabels.length) })}
                    showRange={false}
                    selectable={printLabels.length > 1}
                    groups={printLabels.length > 1 ? TYPE_KEYS.map(k => ({ key: k, label: k })) : []}
                    styles={["tote"]}
                    defaultSize={TOTE_LABEL_BASE}
                    labels={printLabels}
                    widthMm={toteLabelSize.widthMm}
                    heightMm={toteLabelSize.heightMm}
                    onSaveSize={saveSize}
                    onClose={() => setPrintLabels(null)}
                />
            )}
        </div>
    );
};

export default TotesAdmin;
